import {ReactElement, useEffect, useRef, useState} from "react";
import {useIntegrator} from "../integrator.context";

interface ListProps<T> {
    title: string;
    elements: T[];
    component(element: T): ReactElement;
    search?(element: T): string;
    // Optional stable key per element. Without this, the list keys items by
    // array index, which means React's reconciler matches DOM nodes by position
    // instead of identity — so when the sorted order changes, your focused
    // input stays in place but its bound element changes underneath you.
    // Pass element.id / element.uid where possible.
    keyFor?(element: T): string | number;
    sorted?: boolean;
    headerClassName?: string;
    defaultSearchKey?: string;
    // Optional control rendered in the header, just left of the search box
    // (e.g. a "Create new…" button). Sized to its content.
    headerAction?: ReactElement;
}

function List<T>(props: ListProps<T>) {
    const {state} = useIntegrator();
    const {elements: propElements, search, sorted, keyFor} = props;
    const [searchKey, setSearchKey] = useState<string>(props.defaultSearchKey ?? '');
    const [elements, setElements] = useState<T[]>(propElements);

    const hasElements = elements !== undefined && elements.length > 0;

    // Freeze the sort while the user is mid-edit on any item in the list. The
    // sort key (typically a localized name) changes character-by-character as
    // the user types; without this gate the row jumps to its new alphabetical
    // position on every keystroke. We track focus on the *items* container
    // only, so typing in the search box (which is in the header) still
    // re-sorts in real time.
    const itemsRef = useRef<HTMLDivElement>(null);
    const [isItemFocused, setIsItemFocused] = useState(false);

    useEffect(() => {
        const el = itemsRef.current;
        if (!el) return;
        const onFocusIn = () => setIsItemFocused(true);
        const onFocusOut = () => {
            // Focus may be transitioning between sibling inputs; defer the
            // check so we see the final state (browser fires focusout on the
            // old element before focusin on the new one).
            setTimeout(() => {
                if (itemsRef.current && !itemsRef.current.matches(':focus-within'))
                    setIsItemFocused(false);
            }, 0);
        };
        el.addEventListener('focusin', onFocusIn);
        el.addEventListener('focusout', onFocusOut);
        return () => {
            el.removeEventListener('focusin', onFocusIn);
            el.removeEventListener('focusout', onFocusOut);
        };
    }, []);

    useEffect(() => {
        if (propElements === undefined) {
            return;
        }

        const newElements: T[] = [];
        for (const element of propElements) {
            if (searchKey === '' || (search && search(element).toLowerCase().includes(searchKey.toLowerCase()))) {
                newElements.push(element);
            }
        }

        if (isItemFocused) {
            // An item input is focused. Still refresh element contents from
            // props so edits to the focused field render live, but preserve the
            // order that was on screen when focus began — re-sorting now would
            // make the row jump out from under the user mid-edit. Items not
            // present before (or with no stable key) fall to the end in natural
            // order. Order resumes sorting on blur.
            const key = keyFor ?? ((e: T) => e as unknown as string | number);
            const prevOrder = new Map(elements.map((e, i) => [key(e), i] as const));
            newElements.sort((a, b) => {
                const ia = prevOrder.get(key(a)) ?? Number.MAX_SAFE_INTEGER;
                const ib = prevOrder.get(key(b)) ?? Number.MAX_SAFE_INTEGER;
                return ia - ib;
            });
        } else if (search && sorted !== false) {
            newElements.sort((a, b) => search!(a).localeCompare(search!(b)));
        }

        setElements(newElements);
        // `elements` is intentionally omitted from deps: it's read only as the
        // frozen-order snapshot, and including it would loop with setElements.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, searchKey, propElements, search, sorted, isItemFocused, keyFor]);

    return (
        <div className={'layout vertical gap flex-grow'}>
            <div className={'layout horizontal stretch center ' + props.headerClassName ?? ''} style={{overflow: 'hidden'}}>
                <p className={'text header'}>{props.title}</p>
                <div className={'layout horizontal center'} style={{gap: 8, minWidth: 0}}>
                    {props.headerAction && <div style={{flexShrink: 0}}>{props.headerAction}</div>}
                    <input value={searchKey} placeholder={'Search...'} style={{flex: '0 1 100px', minWidth: 0}} onChange={e => setSearchKey(e.target.value)}/>
                </div>
            </div>
            <div ref={itemsRef} className={'layout vertical gap flex-grow'} style={{overflowY: 'auto', height: 0}}>
                {!hasElements && <EmptyElement/>}
                {hasElements && elements.map((element, index) => (
                    <div key={keyFor ? keyFor(element) : index}>
                        {props.component(element)}
                    </div>
                ))}
            </div>
        </div>
    )
}

function EmptyElement() {
    return (
        <div className={'label'} style={{padding: 10}}>
            <p>No elements found.</p>
        </div>
    )
}

export default List;
